import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import {
  getUnplacedDownline,
  placeSlot,
  type UnplacedSlot,
} from '../../lib/api';
import { CreateSlotDialog } from './create-slot-dialog';

export type PlaceSlotDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rootSlotId: string;
  parentSlotId: string;
  parentSlotCode: string;
  position: 'L' | 'R';
  onSuccess?: () => void;
};

/**
 * Opens when the binary genealogy view sees an empty L/R seat clicked.
 * Two paths:
 *   1. Pick an existing unplaced slot from the root's downline → place at L/R.
 *   2. Fall through to `CreateSlotDialog` with `placement` defaults so the new
 *      slot is created AND placed atomically at the same seat.
 *
 * When the unplaced pool is empty, only path (2) is offered.
 */
export function PlaceSlotDialog({
  open,
  onOpenChange,
  rootSlotId,
  parentSlotId,
  parentSlotCode,
  position,
  onSuccess,
}: PlaceSlotDialogProps) {
  const qc = useQueryClient();
  const [pickedSlotId, setPickedSlotId] = useState<string>('');
  const [createFallbackOpen, setCreateFallbackOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The sponsor places their OWN direct downlines into any open seat within
  // their binary subtree (spillover). The pool is therefore scoped to the
  // VIEWED ROOT slot's unplaced directs — not the immediate seat parent.
  const poolQ = useQuery({
    queryKey: ['unplaced-direct', rootSlotId],
    queryFn: () => getUnplacedDownline(rootSlotId),
    enabled: open,
  });

  const pool = useMemo<UnplacedSlot[]>(() => poolQ.data?.nodes ?? [], [poolQ.data]);
  const poolEmpty = !poolQ.isLoading && pool.length === 0;

  useEffect(() => {
    if (open && !pickedSlotId && pool[0]) {
      setPickedSlotId(pool[0].slotId);
    }
  }, [open, pickedSlotId, pool]);

  useEffect(() => {
    if (!open) {
      setPickedSlotId('');
      setError(null);
    }
  }, [open]);

  const mut = useMutation({
    mutationFn: () => placeSlot(pickedSlotId, { parentSlotId, position }),
    meta: { successMessage: 'Slot placed.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-slot-genealogy', rootSlotId] });
      qc.invalidateQueries({ queryKey: ['unplaced-direct', rootSlotId] });
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: string } } };
      const code = e.response?.data?.error ?? 'place_failed';
      setError(prettyPlaceError(code));
    },
  });

  return (
    <>
      <Dialog open={open && !createFallbackOpen} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Place slot</DialogTitle>
            <DialogDescription>
              Place an unplaced slot at the {position === 'L' ? 'LEFT' : 'RIGHT'} seat under{' '}
              <span className="font-mono">{parentSlotCode}</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {poolQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading unplaced slots…</p>
            ) : poolEmpty ? (
              <p className="text-sm text-muted-foreground">
                No unplaced direct downlines for this slot yet. Place one of this
                slot's own direct downlines into any open seat in its tree — create a
                new slot here, or sponsor one first.
              </p>
            ) : (
              <div>
                <label className="text-xs text-muted-foreground">Unplaced slot</label>
                <Select value={pickedSlotId} onValueChange={setPickedSlotId}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pool.map((s) => (
                      <SelectItem key={s.slotId} value={s.slotId}>
                        {s.slotCode} · {s.userFullName ?? s.userEmail ?? '(no name)'} ·{' '}
                        {s.membershipName ?? s.membershipCode ?? '(no tier)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCreateFallbackOpen(true)}
              disabled={mut.isPending}
            >
              Create new slot here →
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={mut.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={() => mut.mutate()}
                disabled={mut.isPending || poolEmpty || !pickedSlotId}
              >
                {mut.isPending ? 'Placing…' : 'Place'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateSlotDialog
        open={createFallbackOpen}
        onOpenChange={(next) => {
          setCreateFallbackOpen(next);
          if (!next) onOpenChange(false);
        }}
        defaults={{
          sponsorCode: parentSlotCode,
          sponsorLocked: true,
          placement: { parentSlotId, position },
        }}
        onSuccess={() => {
          qc.invalidateQueries({ queryKey: ['admin-slot-genealogy', rootSlotId] });
          qc.invalidateQueries({ queryKey: ['unplaced-direct', rootSlotId] });
          onSuccess?.();
        }}
      />
    </>
  );
}

function prettyPlaceError(code: string): string {
  switch (code) {
    case 'already_placed':
      return 'This slot is already placed somewhere in the binary tree.';
    case 'position_occupied':
      return 'That seat was just taken by another action. Refresh and try again.';
    case 'forbidden':
      return 'You don’t have permission to place that slot.';
    case 'seat_not_in_sponsor_subtree':
      return 'That seat isn’t inside this slot’s own binary leg.';
    case 'slot_not_found':
      return 'The slot could not be found.';
    default:
      return code.replace(/_/g, ' ');
  }
}
